import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';
import {from, fromEvent, Subject, Subscription} from 'rxjs';
import {filter, map, mergeMap, take} from 'rxjs/operators';
import {animate, AnimationEvent, state, style, transition, trigger} from '@angular/animations';
import {SidebarDrawer} from './sidebar-drawer.model';
import {Store} from '@ngxs/store';
import Direction = SidebarDrawer.Direction;
import PositionConfigItem = SidebarDrawer.PositionConfigItem;

const animationTiming = '.2s ease-out';

@Component({
    selector: 'app-sidebar-drawer',
    templateUrl: './sidebar-drawer.component.html',
    styleUrls: ['./sidebar-drawer.component.less'],
    animations: [
        trigger('opacity', [
            state('void', style({opacity: 0})),
            state('enter', style({opacity: 1})),
            state('leave', style({opacity: 0})),
            transition('* => *', animate(animationTiming)),
        ]),
        trigger('slideContent', [
            state('void', style({transform: 'translate({{translateSign}}100%)'}), {params: {translateSign: '+'}}),
            state('enter', style({transform: 'translateX(0)', boxShadow: '{{boxShadow}}'}), {params: {boxShadow: ''}}),
            state('leave', style({transform: 'translate({{translateSign}}100%)'}), {params: {translateSign: '+'}}),
            transition('void <=> leave', animate(0)),
            transition('* => *', animate(animationTiming)),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SidebarDrawerComponent implements AfterViewInit, OnDestroy, PositionConfigItem {
    animationState: 'leave' | 'void' | 'enter' | any = 'void';
    animationState$ = new Subject<AnimationEvent>();
    _layoutConfig: PositionConfigItem;
    @Input()
    set layoutConfig(newValue: PositionConfigItem) {
        this._layoutConfig = newValue;
        this.assignLayoutConfig(this._layoutConfig);
    }

    direction: Direction = Direction.left;
    backdrop = false;
    fullwidth = false;
    padded = false;
    @ViewChild('boundaryElement', {read: ElementRef}) boundaryElement: ElementRef;
    subscriptions = new Subscription();
    protected _isOpen = false;
    @Input() set isOpen(newValue: boolean) {
        this._isOpen = newValue;
        if (this._isOpen) {
            this.animationState = 'enter';
            this.isClosed = false;
            this.cleanupCloseSubscription();
        } else {
            this.onClose();
        }
    }

    get isOpen() {
        return this._isOpen;
    }

    @Output() isOpenChange = new EventEmitter<boolean>();
    isClosed = true;
    protected closeSubscription: Subscription;
    @Input() trigger: ElementRef;
    backdropClickable = false;

    constructor(
        protected cd: ChangeDetectorRef,
        protected store: Store
    ) {}

    get translateSign() {
        return this.direction === Direction.left ? '-' : '+';
    }

    get boxShadow() {
        return !this.fullwidth && !this.backdrop ? `0 10px 20px 0 #f0f0f0` : '';
    }

    ngAfterViewInit() {
        this.subscriptions.add(this.closeDrawerChanges());
    }

    onAnimationEvent(event: AnimationEvent) {
        this.animationState$.next(event);
    }

    startExitAnimation() {
        this.animationState = 'leave';
        this.cd.markForCheck();
    }

    onClose() {
        if (!this.isClosed) {
            this.cleanupCloseSubscription();
            // Listen for animation 'done'
            this.closeSubscription = this.animationState$
                .pipe(
                    filter((event) => event.phaseName === 'done' && event.toState === 'leave'),
                    take(1)
                )
                .subscribe(() => {
                    this.isClosed = true;
                    this.isOpenChange.emit(false);
                    this._isOpen = false;
                });
        }
        // Start exit animation
        this.startExitAnimation();
    }

    protected closeDrawerChanges(): Subscription {
        return from(['click', 'touchstart'])
            .pipe(
                mergeMap((event) => fromEvent(document, event, {capture: true})),
                filter(() => this.isOpen),
                map((event) => {
                    return (
                        !this.boundaryElement.nativeElement.contains(event.target) &&
                        !this.trigger?.nativeElement?.contains(event.target)
                    );
                }),
                filter(Boolean)
            )
            .subscribe(() => {
                this.onClose();
            });
    }

    protected cleanupCloseSubscription() {
        if (this.closeSubscription && !this.closeSubscription.closed) {
            this.closeSubscription.unsubscribe();
        }
    }

    protected assignLayoutConfig(config?: PositionConfigItem) {
        this.backdrop = config?.backdrop ?? false;
        this.direction = config?.direction ?? Direction.left;
        this.fullwidth = config?.fullwidth ?? false;
        this.padded = config?.padded ?? false;
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        this.cleanupCloseSubscription();
    }

    onMouseEnter() {
        if (!this.isOpen) {
            this.isOpen = true;
        }
    }

    onOpacityAnimation(event: AnimationEvent) {
        this.backdropClickable =
            event.toState === 'enter' || (event.toState === 'leave' && event.phaseName === 'start');
        this.cd.markForCheck();
    }
}
